// app/ui/src/components/TrackingHistory.js
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchTrackingHistory } from '../store/slices/trackingHistorySlice';
import { 
  MapPin, 
  Clock, 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward,
  Rewind,
  FastForward,
  Activity,
  Navigation,
  Fuel,
  Smartphone,
  Radio,
  Compass,
  Power
} from 'lucide-react';
import { formatDateTime } from '../utils/formatters';

const TrackingHistory = ({ loadId, onTimelineSelect, selectedTimelineIndex = -1 }) => {
  const dispatch = useDispatch();
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(2000); // milliseconds between steps
  const [localSelectedIndex, setLocalSelectedIndex] = useState(-1);
  
  const { history, loading } = useSelector((state) => state.trackingHistory);
  const loadHistory = history[loadId] || [];

  // Use external selectedTimelineIndex if provided, otherwise use local state
  const currentSelectedIndex = selectedTimelineIndex !== -1 ? selectedTimelineIndex : localSelectedIndex;

  // Calculate distance between two points (in miles) - defined early to avoid hoisting issues
  const calculateDistance = React.useCallback((lat1, lon1, lat2, lon2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }, []);

  useEffect(() => {
    dispatch(fetchTrackingHistory({ loadId }));
  }, [dispatch, loadId]);

  // Auto-playback functionality
  useEffect(() => {
    let interval;
    if (isPlaying && loadHistory.length > 0) {
      interval = setInterval(() => {
        setLocalSelectedIndex(prev => {
          const nextIndex = prev + 1;
          if (nextIndex >= loadHistory.length) {
            setIsPlaying(false);
            return loadHistory.length - 1;
          }
          
          // Notify parent component of automatic timeline progression
          // Use setTimeout to ensure state update completes first
          setTimeout(() => {
            if (onTimelineSelect && typeof onTimelineSelect === 'function' && loadHistory[nextIndex]) {
              onTimelineSelect(nextIndex, loadHistory[nextIndex]);
            }
          }, 0);
          
          return nextIndex;
        });
      }, playbackSpeed);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isPlaying, playbackSpeed, loadHistory.length, onTimelineSelect]);

  // Enhanced timeline data with interpolated journey
  const timelineData = useMemo(() => {
    if (!loadHistory.length) return [];
    
    return loadHistory.map((update, index) => ({
      ...update,
      index,
      isSelected: index === currentSelectedIndex,
      isViewed: index <= currentSelectedIndex,
      relativeTime: index === 0 ? 0 : 
        (new Date(update.created_at) - new Date(loadHistory[0].created_at)) / (1000 * 60), // minutes from start
      distanceFromPrevious: index === 0 || !loadHistory[index - 1] ? 0 : calculateDistance(
        loadHistory[index - 1].latitude,
        loadHistory[index - 1].longitude,
        update.latitude,
        update.longitude
      )
    }));
  }, [loadHistory, currentSelectedIndex, calculateDistance]);

  const handleTimelineSelect = (index) => {
    setLocalSelectedIndex(index);
    setIsPlaying(false);
    // Only call onTimelineSelect if it's provided (maintains backward compatibility)
    if (onTimelineSelect && typeof onTimelineSelect === 'function') {
      onTimelineSelect(index, loadHistory[index]);
    }
  };

  const handlePlayPause = () => {
    if (currentSelectedIndex >= loadHistory.length - 1) {
      // Reset to beginning
      setLocalSelectedIndex(0);
      if (onTimelineSelect && typeof onTimelineSelect === 'function' && loadHistory[0]) {
        onTimelineSelect(0, loadHistory[0]);
      }
    } else if (currentSelectedIndex === -1) {
      // Start from beginning if no point is selected
      setLocalSelectedIndex(0);
      if (onTimelineSelect && typeof onTimelineSelect === 'function' && loadHistory[0]) {
        onTimelineSelect(0, loadHistory[0]);
      }
    }
    setIsPlaying(!isPlaying);
  };

  const handleSpeedChange = (speed) => {
    setPlaybackSpeed(speed);
  };

  const formatTimestamp = (timestamp) => {
    return formatDateTime(timestamp);
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  if (loading && !loadHistory.length) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg h-full flex flex-col">
      {/* Header with Controls */}
      <div className="p-3 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold flex items-center gap-2">
            <Navigation className="w-4 h-4 text-blue-500" />
            Journey Timeline
          </h3>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
              {loadHistory.length} points
            </span>
          </div>
        </div>

        {/* Playback Controls */}
        {loadHistory.length > 0 && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <button
                onClick={() => handleTimelineSelect(0)}
                className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                title="Go to start"
              >
                <SkipBack className="w-3.5 h-3.5" />
              </button>
              
              <button
                onClick={() => handleTimelineSelect(Math.max(0, currentSelectedIndex - 1))}
                className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                title="Previous point"
                disabled={currentSelectedIndex <= 0}
              >
                <Rewind className="w-3.5 h-3.5" />
              </button>

              <button
                onClick={handlePlayPause}
                className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-full transition-colors"
                title={isPlaying ? "Pause playback" : "Play journey"}
              >
                {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
              </button>

              <button
                onClick={() => handleTimelineSelect(Math.min(loadHistory.length - 1, currentSelectedIndex + 1))}
                className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                title="Next point"
                disabled={currentSelectedIndex >= loadHistory.length - 1}
              >
                <FastForward className="w-3.5 h-3.5" />
              </button>

              <button
                onClick={() => handleTimelineSelect(loadHistory.length - 1)}
                className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                title="Go to latest"
              >
                <SkipForward className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Speed Controls */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-500">Speed:</span>
              {[
                { label: '0.5x', value: 4000 },
                { label: '1x', value: 2000 },
                { label: '2x', value: 1000 },
                { label: '4x', value: 500 }
              ].map(({ label, value }) => (
                <button
                  key={label}
                  onClick={() => handleSpeedChange(value)}
                  className={`px-1.5 py-0.5 text-xs rounded transition-colors ${
                    playbackSpeed === value
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Visual Timeline Scrubber */}
      {loadHistory.length > 0 && (
        <div className="px-3 py-2 bg-gradient-to-r from-slate-50 to-blue-50/30 border-b border-gray-100 flex-shrink-0">
          <div className="relative">
            {/* Timeline track */}
            <div className="h-1.5 bg-gray-200 rounded-full relative overflow-hidden">
              {/* Progress indicator */}
              <div
                className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full transition-all duration-300"
                style={{
                  width: currentSelectedIndex >= 0 
                    ? `${((currentSelectedIndex + 1) / loadHistory.length) * 100}%`
                    : '0%'
                }}
              />
            </div>

            {/* Timeline points - show less on smaller screens */}
            <div className="flex justify-between mt-1.5">
              {timelineData.map((point, index) => {
                // Show fewer points on the scrubber for cleaner UI
                const showPoint = loadHistory.length <= 20 || 
                  index % Math.max(1, Math.floor(loadHistory.length / 15)) === 0 || 
                  point.isSelected ||
                  index === 0 ||
                  index === loadHistory.length - 1;
                
                if (!showPoint) return null;
                
                return (
                  <motion.button
                    key={index}
                    onClick={() => handleTimelineSelect(index)}
                    className="relative group"
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <div
                      className={`w-2 h-2 rounded-full border transition-all duration-200 ${
                        point.isSelected
                          ? 'bg-blue-500 border-blue-600 shadow-md scale-125'
                          : point.isViewed
                          ? 'bg-blue-300 border-blue-400'
                          : 'bg-white border-gray-300 hover:border-blue-300'
                      }`}
                    />
                    
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                      <div className="bg-gray-900 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap shadow-lg">
                        {formatTimestamp(point.created_at)}
                        <br />
                        Point {index + 1}
                      </div>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Current Point Details - Compact version */}
      <AnimatePresence>
        {currentSelectedIndex >= 0 && loadHistory[currentSelectedIndex] && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="border-t border-gray-200 flex-shrink-0"
          >
            <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                    <MapPin className="w-3 h-3 text-white" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">
                      Point {currentSelectedIndex + 1} of {loadHistory.length}
                    </h4>
                    <p className="text-xs text-gray-600">
                      {formatTimestamp(loadHistory[currentSelectedIndex].created_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Source Badge */}
                  {loadHistory[currentSelectedIndex].source === 'samsara' ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] rounded-full">
                      <Radio className="w-2.5 h-2.5" />
                      Samsara
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] rounded-full">
                      <Smartphone className="w-2.5 h-2.5" />
                      Driver App
                    </span>
                  )}
                  {timelineData[currentSelectedIndex]?.relativeTime > 0 && (
                    <span className="text-xs font-medium text-blue-600">
                      +{formatDuration(timelineData[currentSelectedIndex].relativeTime)}
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-xs">
                <div className="col-span-2">
                  <span className="text-gray-500 text-[10px]">Location</span>
                  <p className="font-mono text-[11px] text-gray-700">
                    {loadHistory[currentSelectedIndex].latitude.toFixed(5)}, {loadHistory[currentSelectedIndex].longitude.toFixed(5)}
                  </p>
                </div>
                {loadHistory[currentSelectedIndex].speed != null && (
                  <div>
                    <span className="text-gray-500 text-[10px]">Speed</span>
                    <p className="flex items-center gap-0.5 font-medium text-gray-700">
                      <Activity className="w-3 h-3 text-gray-400" />
                      {loadHistory[currentSelectedIndex].speed.toFixed(0)} mph
                    </p>
                  </div>
                )}
                {loadHistory[currentSelectedIndex].heading != null && (
                  <div>
                    <span className="text-gray-500 text-[10px]">Heading</span>
                    <p className="flex items-center gap-0.5 font-medium text-gray-700">
                      <Compass className="w-3 h-3 text-gray-400" />
                      {loadHistory[currentSelectedIndex].heading.toFixed(0)}°
                    </p>
                  </div>
                )}
                {loadHistory[currentSelectedIndex].fuel_percent != null && (
                  <div>
                    <span className="text-gray-500 text-[10px]">Fuel</span>
                    <p className="flex items-center gap-0.5 font-medium text-gray-700">
                      <Fuel className="w-3 h-3 text-gray-400" />
                      {loadHistory[currentSelectedIndex].fuel_percent.toFixed(0)}%
                    </p>
                  </div>
                )}
                {loadHistory[currentSelectedIndex].engine_state && (
                  <div>
                    <span className="text-gray-500 text-[10px]">Engine</span>
                    <p className="flex items-center gap-0.5 font-medium text-gray-700">
                      <Power className="w-3 h-3 text-gray-400" />
                      {loadHistory[currentSelectedIndex].engine_state}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Condensed History List - scrollable with flex-1 */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Recent Points</span>
            {loadHistory.length > 10 && (
              <span className="text-[10px] text-gray-400">
                Showing latest {Math.min(10, loadHistory.length)} of {loadHistory.length}
              </span>
            )}
          </div>
          <div className="space-y-1">
            {timelineData.slice(-10).reverse().map((update, reverseIndex) => {
              const actualIndex = loadHistory.length - 1 - reverseIndex;
              return (
                <motion.div
                  key={actualIndex}
                  onClick={() => handleTimelineSelect(actualIndex)}
                  className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all ${
                    update.isSelected
                      ? 'bg-blue-50 border-l-3 border-blue-500 shadow-sm'
                      : 'hover:bg-gray-50 border-l-3 border-transparent'
                  }`}
                  whileHover={{ x: 2 }}
                >
                  <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                    update.isViewed ? 'bg-blue-500' : 'bg-gray-300'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      <span className="text-[11px] text-gray-600 font-medium">
                        {formatTimestamp(update.created_at)}
                      </span>
                      {/* Source indicator */}
                      {update.source === 'samsara' ? (
                        <Radio className="w-3 h-3 text-emerald-500 flex-shrink-0" title="Samsara ELD" />
                      ) : (
                        <Smartphone className="w-3 h-3 text-blue-500 flex-shrink-0" title="Driver App" />
                      )}
                      {/* Speed indicator */}
                      {update.speed != null && (
                        <span className="text-[10px] text-gray-400 ml-auto flex-shrink-0">
                          {update.speed.toFixed(0)} mph
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-gray-400 truncate mt-0.5 font-mono">
                      {update.latitude.toFixed(5)}, {update.longitude.toFixed(5)}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

    </div>
  );
};

export default TrackingHistory;